import React, { useEffect, useMemo, useState } from 'react'
import merge from 'lodash/merge'
import remoteConfigService from '../services/remote-config-service'
import defaultConfig from '../constants/config/default-config'
import { SITE_ID } from '../constants'
import { ConfigParams, siteIds, SiteIds, environments as defaultEnvs, Environments } from './types'
import { getCurrentEnv, getValue } from './helpers'

export type Config = ConfigParams

export interface ConfigContextValue {
  configInitialized: boolean
  config: Config
  configByEnv: { [environment: string]: { [siteId: string]: Config } }
  configBySiteId: { [key: string]: Config }
  environments: string[]
  environment: string
  siteIds: string[]
  siteId: string
}

export const ConfigContext = React.createContext<ConfigContextValue>({
  configInitialized: false,
  config: defaultConfig,
  configByEnv: {},
  configBySiteId: {},
  environments: [],
  environment: '',
  siteIds: [],
  siteId: '',
})

const siteId = SITE_ID || 'BMAR'

const cleanConfig = (config) =>
  Object.fromEntries(
    Object.entries(config || {}).filter(
      ([key]) => key !== 'environments' && !siteIds.includes(SiteIds[key]) && !defaultEnvs.includes(Environments[key])
    )
  )

const getSplicedConfig = (remoteConfigAvailable, config, currentEnvs, currentEnv, currentSiteIds, currentSiteId) => {
  const defaultConfigPart = [
    cleanConfig(defaultConfig),
    cleanConfig(defaultConfig?.[currentSiteId]),
    cleanConfig(defaultConfig?.[currentEnv]),
    cleanConfig(defaultConfig?.[currentEnv]?.[currentSiteId]),
  ]

  if (!remoteConfigAvailable) {
    return defaultConfigPart
  }

  const envGroup = `(${currentEnvs.join('|')})`
  const currentSiteIdsGroup = `(${currentSiteIds.join('|')})`

  const keyPattern = `((?!${envGroup}|${currentSiteIdsGroup}).)*`

  const envPattern = `_${currentEnv}`
  const siteIdPattern = `_${currentSiteId}`
  const envSiteIdPattern = `${envPattern}${siteIdPattern}`
  const suffixPattern = `${envSiteIdPattern}|${envPattern}|${siteIdPattern}`

  const normalRegExp = new RegExp(`^${keyPattern}$`, 'i')
  const envRegExp = new RegExp(`^${keyPattern}${envPattern}$`, 'i')
  const siteIdRegExp = new RegExp(`^${keyPattern}${siteIdPattern}$`, 'i')
  const envSiteIdRegExp = new RegExp(`^${keyPattern}${envSiteIdPattern}$`, 'i')
  const suffixRegExp = new RegExp(`${suffixPattern}$`, 'i')

  const getFilteredConfig = (filterRegExp: RegExp) =>
    Object.fromEntries(
      Object.entries(config)
        ?.filter(([key]) => filterRegExp.test(key))
        ?.map(([key, value]) => [key.replace(suffixRegExp, ''), value])
    )

  const remoteConfigPart = [
    getFilteredConfig(normalRegExp),
    getFilteredConfig(envRegExp),
    getFilteredConfig(siteIdRegExp),
    getFilteredConfig(envSiteIdRegExp),
  ]

  return [...defaultConfigPart, ...remoteConfigPart]
}

export const ConfigContextProvider = ({ children }) => {
  const [remoteConfigStatus, setRemoteConfigStatus] = useState({
    initialized: false,
    error: false,
    values: remoteConfigService.getAll(),
  })

  const configInitialized = remoteConfigStatus.initialized || remoteConfigStatus.error
  const remoteConfigAvailable = remoteConfigStatus.initialized && !remoteConfigStatus.error

  const rawEnvironments = useMemo(
    () =>
      remoteConfigStatus?.values?.['environments']
        ? getValue(remoteConfigStatus?.values?.['environments'])
        : defaultConfig?.['environments'],
    [remoteConfigStatus?.values]
  )

  const environments = useMemo(() => rawEnvironments?.map(([env]) => env) || [], [rawEnvironments])

  const environment = useMemo(() => getCurrentEnv({ environments: rawEnvironments })!, [rawEnvironments])

  const remoteConfig = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(remoteConfigStatus?.values).map(([key, value]) => {
          if (value.getSource() === 'remote') {
            return [key, getValue(value)]
          } else {
            return [key, value]
          }
        })
      ),
    [remoteConfigStatus?.values]
  )

  const configByEnv: { [environment: string]: { [siteId: string]: Config } } = useMemo(
    () =>
      Object.fromEntries<{ [siteId: string]: Config }>(
        environments.map(
          (env) =>
            [
              env,
              Object.fromEntries<Config>(
                siteIds.map((id) => {
                  const siteIdSplicedConfig = getSplicedConfig(
                    remoteConfigAvailable,
                    remoteConfig,
                    environments,
                    env,
                    siteIds,
                    id
                  )
                  const siteIdConfig: Config = cleanConfig(merge({}, ...siteIdSplicedConfig)) as any
                  return [id, siteIdConfig] as [SiteIds, Config]
                })
              ),
            ] as any
        )
      ),
    [remoteConfigAvailable, remoteConfig, environments, siteIds]
  )

  const configBySiteId = useMemo(() => configByEnv?.[environment], [environment, configByEnv])
  const config = useMemo(() => configBySiteId?.[siteId], [siteId, configBySiteId])

  useEffect(() => {
    const listener = () => {
      const values = remoteConfigService.getAll()
      setRemoteConfigStatus({
        initialized: remoteConfigService.initialized,
        error: remoteConfigService.error,
        values,
      })
    }
    remoteConfigService.addEventListener(listener)
    remoteConfigService.ensureInitialized().then(() => {
      const values = remoteConfigService.getAll()
      setRemoteConfigStatus({
        initialized: remoteConfigService.initialized,
        error: remoteConfigService.error,
        values,
      })
    })
    return () => {
      remoteConfigService.removeEventListener(listener)
    }
  }, [])

  const contextValue = {
    configInitialized,
    config,
    configByEnv,
    configBySiteId,
    environments,
    environment,
    siteIds,
    siteId,
  }

  return <ConfigContext.Provider value={contextValue}>{children}</ConfigContext.Provider>
}
